/**
 * The three classes, one per combat archetype.
 *
 * `docs/GAME-DESIGN.md`: *"Melee, caster, ranged. Three skill trees each.
 * Deliberately covers all three combat archetypes so the engine gets exercised
 * properly rather than overfitting to one."*
 */
export const CHARACTER_CLASSES = Object.freeze(['melee', 'caster', 'ranged']);
/** Whether this is one of the three. Narrow enough to use as a type guard on request input. */
export const isCharacterClass = (value) => typeof value === 'string' && CHARACTER_CLASSES.includes(value);
/** The class a client asked for, or a refusal. A class is a choice, so a bad one is an ordinary outcome. */
export const parseCharacterClass = (value) => isCharacterClass(value) ? { ok: true, characterClass: value } : { ok: false, reason: 'unknown-class' };
/** Shortest name worth having. Two, so that initials work and one stray keystroke does not. */
export const MIN_CHARACTER_NAME_LENGTH = 2;
/**
 * Longest name, in graphemes.
 *
 * The same number `apps/game`'s `MAX_NAME_LENGTH` clamps a nameplate to, and it
 * is that constant's source — a name the API accepts and the wire truncates
 * would be a character whose nameplate is not its name.
 */
export const MAX_CHARACTER_NAME_LENGTH = 24;
/**
 * How many characters one account may hold.
 *
 * D2's own eight. It is a resource bound as much as a game rule: every character
 * carries an inventory, a belt, a stash and their contents, and an unbounded
 * count is an unbounded row count bought with one registration.
 */
export const MAX_CHARACTERS_PER_ACCOUNT = 8;
/**
 * The stash, in cells. Six by eight is D2's own.
 *
 * It has to be bigger than the ten-by-four pack or it is not storage. **Where
 * this belongs is core**, beside `INVENTORY_WIDTH` — it is a `createInventory`
 * argument and nothing else — and it is here because this branch may not write
 * there (`TODO.md` #393).
 */
export const STASH_WIDTH = 6;
export const STASH_HEIGHT = 8;
/**
 * Letters, marks, digits, and the two separators D2 allowed.
 *
 * Marks are in the list for the same reason the count is in graphemes: `e` plus
 * a combining acute is one letter to a reader, and a whitelist without `\p{M}`
 * refuses half the alphabets in Europe. Everything invisible is refused by being
 * absent — a zero-width joiner and a right-to-left override are format
 * characters, and neither is a letter, a mark, a digit, a hyphen or an
 * underscore.
 */
const NAME_CHARACTERS = /^[\p{L}\p{M}\p{N}_-]+$/u;
/** Counts a name the way a reader does, exactly as the wire's own clamp is measured. */
const GRAPHEMES = new Intl.Segmenter();
const SEPARATORS = ['-', '_'];
const edgedBySeparator = (name) => SEPARATORS.some((separator) => name.startsWith(separator) || name.endsWith(separator));
/**
 * A name fit to store, or the reason it is not.
 *
 * Trimmed at the edges — a leading space is a typo, not a name — and then held
 * to a whitelist rather than a blacklist, because the interesting attacks on a
 * name are all characters nobody thinks to ban: an invisible one that makes two
 * nameplates identical, a right-to-left override that reverses what a reader
 * sees, a slash that would turn an id into a second path segment.
 *
 * A separator at either end is refused so that `-` cannot stand in for the
 * leading space that was just trimmed away.
 */
export const parseCharacterName = (value) => {
    if (typeof value !== 'string')
        return { ok: false, reason: 'malformed' };
    const name = value.trim();
    const graphemes = [...GRAPHEMES.segment(name)].length;
    if (graphemes < MIN_CHARACTER_NAME_LENGTH)
        return { ok: false, reason: 'too-short' };
    if (graphemes > MAX_CHARACTER_NAME_LENGTH)
        return { ok: false, reason: 'too-long' };
    if (!NAME_CHARACTERS.test(name))
        return { ok: false, reason: 'malformed' };
    if (edgedBySeparator(name))
        return { ok: false, reason: 'malformed' };
    return { ok: true, name };
};
/**
 * Whether the database has somewhere to put this.
 *
 * The one place in the codebase that draws the line between `CarriedThing` and
 * `StoredThing`, and it draws it by naming the two things that **are** storable
 * rather than the one that is not. Gear carries a `rarity` — the same marker
 * `apps/api`'s own loader tells the two tables apart by — and a potion carries a
 * `kind`. A `RespecScroll` has neither: core mints it from a base and an item
 * level and its every field is one a potion also has, so a check written the
 * other way round (`'value' in thing`) would quietly classify every potion as a
 * scroll and stop saving the belt.
 *
 * Naming the storable side is also the safe direction for the *next* thing a
 * grid learns to hold: something new is not storable until somebody says it is.
 */
const isStorable = (thing) => 'rarity' in thing || 'kind' in thing;
/**
 * The same grid with everything the database cannot hold left out of it.
 *
 * The cells of what remains are **untouched**. A filter that repacked would move
 * every item a player owns, which is a far worse bug than the one it is working
 * around: `Inventory` is `{ width, height, items }`, so a shorter `items` is a
 * valid grid describing exactly the same layout minus one thing.
 */
const storable = (grid) => ({
    width: grid.width,
    height: grid.height,
    items: grid.items.flatMap((placed) => (isStorable(placed.item) ? [{ item: placed.item, at: placed.at }] : [])),
});
/**
 * The write a character that has done nothing would produce.
 *
 * One definition of "the containers, as they now stand", shared by the snapshot
 * that was loaded and the state that goes back — so a save is provably the same
 * shape as the load and a round trip cannot quietly drop a container.
 *
 * The gold comes from the **purse**, not from the summary's `gold`. The summary
 * is what was loaded; the purse is what is carried now, and core's `deposit` is
 * what moved it. A save that quoted the summary would write the balance back
 * exactly as it was found, however much the run picked up — which is the kind of
 * bug that looks like nothing at all until a player notices their coins are
 * gone.
 *
 * **Progression is deliberately not carried across**, and it is the same trap
 * one paragraph up rather than an omission. A snapshot's `progression` is what
 * was *loaded*; what a run earned lives in a `CharacterProgress` the room holds
 * and this function has never seen. Echoing the loaded one would write back the
 * level the character started with, however many they gained — the stale-summary
 * bug in its sharper form, because a level-up is not recoverable by picking the
 * coins up again.
 *
 * So the safe default is to say nothing, which the store reads as "leave it
 * alone", and a caller that has run progression adds it deliberately:
 * `{ ...characterStateOf(character), progression }`. That makes writing
 * progression a decision at the call site instead of an accident here.
 *
 * **Respec scrolls do not go home** (`TODO.md` #457). `apps/api` rebuilds every
 * row of a body before it accepts it and answers 400 `unloadable` for one it
 * cannot, and `saves.ts` treats a refusal as permanent — so a single scroll in a
 * grid would end persistence for that seat, gold and gear included. Dropping it
 * from the write is a real loss and the lesser one, and it is temporary: nothing
 * can *use* a scroll yet, so the row that gives one a table lands with the
 * intent that spends it.
 */
export const characterStateOf = (character) => ({
    level: character.level,
    gold: character.purse.carried,
    inventory: storable(character.inventory),
    stash: storable(character.stash),
    belt: character.belt,
    equipment: character.equipment,
});
//# sourceMappingURL=characters.js.map